import React from 'react';
import { STATUS_CIRCLE_TONE } from '../../utils/riskColors';

/**
 * StatusCircle — the portal's single "click to classify" control.
 *
 * Standardised in v1.139.0. Before it, the same gesture wore three different
 * costumes: a filled circle (IP Addresses), a labelled pill (URL Clicks) and a
 * text chip (sessions), which meant an analyst had to re-learn the affordance
 * per table and, in the sessions case, missed it entirely.
 *
 * House rules this component exists to enforce:
 *   - ONE shape, in the LEADING Status column of the table.
 *   - Colour is the whole signal. Tones come from `STATUS_CIRCLE_TONE`, and each
 *     domain maps its own vocabulary onto them (`ipStatusTone`, `urlStatusTone`,
 *     `sessionStatusTone`). Green = the analyst cleared it, red = condemned.
 *   - The state must ALSO be readable without colour — hence a required `title`,
 *     which is the only thing a colour-blind or hurried analyst has to go on.
 *
 * Renders a <button> when clickable and a <span> otherwise, so a derived state
 * (e.g. a compromised session, which the analyst cannot clear here) is visibly
 * inert rather than a button that silently does nothing.
 */
export default function StatusCircle({
  tone = 'gray',
  onClick,
  title,
  disabled = false,
  className = '',
}) {
  const circle = (
    <span
      className={`inline-block h-4 w-4 rounded-full border-2 transition-colors ${
        STATUS_CIRCLE_TONE[tone] || STATUS_CIRCLE_TONE.gray
      } ${onClick && !disabled ? 'group-hover:opacity-80' : ''}`}
    />
  );

  if (!onClick || disabled) {
    return (
      <span className={`p-1 inline-flex ${className}`} title={title}>
        {circle}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      className={`p-1 rounded transition-colors relative group ${className}`}
      title={title}
    >
      {circle}
    </button>
  );
}
